import { computed, inject, reactive, readonly } from "vue";
import { type Store, storeKey } from "vuex";
import { isSupportedDialpadBrowser } from "@/lib/dialpadBrowser";
import { toE164 } from "@/lib/dialpadCti";
import { useOptionalFeatureFlag } from "@/lib/useFeatureFlag";

// Where in Sera a call was started from.
export type DialpadCallSource = "customer" | "job" | "appointment";

export interface DialpadCallContext {
  source: DialpadCallSource;
  sourceId: number;
}

export interface DialpadPendingCall {
  phoneNumber: string;
  context: DialpadCallContext;
  // Placed even though another Dialpad user is logged in: the user chose to.
  confirmed?: boolean;
}

// One dialer per page. The click-to-call buttons live in the routed app and
// the dialer in the app header, which are separate Vue apps, so the shared
// state is module-level rather than per component.
const state = reactive({
  open: false,
  pendingCall: null as DialpadPendingCall | null,
  queuedAt: 0
});

// The signed-in admin's Dialpad user, published by the softphone from the page
// payload. Undefined until published, so a page without the softphone keeps
// today's behaviour; null means the admin has no Dialpad user and may not call.
const mapping = reactive({
  dialpadUserId: undefined as string | null | undefined
});

export function setMappedDialpadUser(dialpadUserId: string | null) {
  mapping.dialpadUserId = dialpadUserId;
}

export function resetMappedDialpadUser() {
  mapping.dialpadUserId = undefined;
}

export function useMappedDialpadUser() {
  return computed(() => mapping.dialpadUserId);
}

const isUnmapped = () => mapping.dialpadUserId === null;

// Whether the dialer is logged in as a Dialpad user other than the admin's own,
// published by the dialer, and the call waiting for the admin to confirm it.
const mismatch = reactive({
  dialerMismatched: false,
  confirmingCall: null as DialpadPendingCall | null
});

export function setDialerMismatched(mismatched: boolean) {
  mismatch.dialerMismatched = mismatched;
}

export function resetMismatchConfirm() {
  mismatch.dialerMismatched = false;
  mismatch.confirmingCall = null;
}

// How long a click waits for Dialpad to be ready before it is dropped, so a
// call never goes out long after the click.
export const HELD_CALL_MS = 15_000;

// Sent with the call as Dialpad's custom_data, which Dialpad passes back in its
// call events. Lets a call be tied back to the Sera record it came from.
export function customDataFor(context: DialpadCallContext): string {
  return JSON.stringify({
    sera_source: context.source,
    sera_source_id: context.sourceId
  });
}

function queueCall(call: DialpadPendingCall) {
  state.pendingCall = call;
  state.queuedAt = Date.now();
  state.open = true;
}

// Context bridge between Sera's click-to-call buttons and the softphone.
export function useDialpadContext() {
  // Queues the call and opens the dialer, which places it once Dialpad is
  // ready. While another Dialpad user is logged in, it asks the admin first.
  // @return false when the number cannot be dialled.
  function startCall(phoneNumber: string, context: DialpadCallContext) {
    const e164 = toE164(phoneNumber);
    if (!e164 || isUnmapped()) return false;

    const call = { phoneNumber: e164, context };
    if (mismatch.dialerMismatched) {
      mismatch.confirmingCall = call;
    } else {
      queueCall(call);
    }
    return true;
  }

  // The queued call, once; null when it waited longer than HELD_CALL_MS.
  function takePendingCall(): DialpadPendingCall | null {
    const call = state.pendingCall;
    state.pendingCall = null;
    return call && Date.now() - state.queuedAt < HELD_CALL_MS ? call : null;
  }

  // Closing the dialer abandons a call still waiting for Dialpad.
  function setOpen(open: boolean) {
    state.open = open;
    if (!open) state.pendingCall = null;
  }

  return {
    isOpen: computed(() => state.open),
    pendingCall: computed(() => readonly(state).pendingCall),
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!state.open),
    startCall,
    takePendingCall
  };
}

// The question asked before a call while another Dialpad user is logged in.
// Either answer opens the dialer, whose warning says how to log in as yourself.
export function useMismatchConfirm() {
  const { open } = useDialpadContext();

  function confirmCall() {
    const call = mismatch.confirmingCall;
    mismatch.confirmingCall = null;
    if (call) queueCall({ ...call, confirmed: true });
  }

  // Leaves any queued call alone: the modal also reports a dismissal after
  // Call anyway has queued it.
  function cancelCall() {
    mismatch.confirmingCall = null;
    open();
  }

  return {
    confirmingCall: computed(() => readonly(mismatch).confirmingCall),
    confirmCall,
    cancelCall
  };
}

// A click queued before the dialer knew who was logged in, found to be another
// Dialpad user's: it asks rather than calling or dropping it.
export function askToConfirm(call: DialpadPendingCall) {
  mismatch.confirmingCall = call;
}

interface SoftphoneTenantState {
  tenant?: { dialpad_cti_origin?: string | null } | null;
}

// The softphone is on only once the tenant has connected Dialpad with call
// logging on, so every Dialpad call made from Sera is also recorded in Sera. The
// tenant payload carries the dialer's origin only then. An admin with no Dialpad
// user, or in a browser the dialer can't run in, gets the numbers as they are
// without the softphone.
export function useSoftphoneEnabled() {
  const store = inject<Store<SoftphoneTenantState> | null>(storeKey, null);
  const softphone = useOptionalFeatureFlag("dialpad_softphone_ff_enabled");
  const integration = useOptionalFeatureFlag("dialpad_integration_ff_enabled");
  const supportedBrowser = isSupportedDialpadBrowser();
  const connected = computed(() =>
    Boolean(store?.state.tenant?.dialpad_cti_origin)
  );
  return computed(
    () =>
      supportedBrowser &&
      softphone.value &&
      integration.value &&
      connected.value &&
      !isUnmapped()
  );
}
